<template>
    <div v-if="show" :id="id"
        class="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center transition delay-100 duration-500 ease-in-out">

        <div class="bg-white w-[95%] rounded-md relative overflow-hidden transition delay-100 duration-500 ease-in-out flex flex-col"
            v-bind:class="[{ '-translate-x-0': popUp, '-translate-y-full': !popUp }, default_width, default_height]">



            <div v-if="closable"
                class="text-md cursor-pointer text-white absolute top-4 right-4 z-10 bg-white rounded-full p-2 hover:bg-gray-50"
                @click="close">
                <img src="@/assets/images/papsi_close.svg" alt="Close" class="w-6 h-6" />
            </div>

            <!-- Icon and Title -->
            <div v-if="error || success || note || question"
                class="w-full flex flex-col items-center justify-center pt-5 p-2 lg:p-5 pb-1 space-y-3">

                <div class="rounded-full p-4 max-w-[80px] max-h-[80px]" :class="{
                    'bg-red-100': error,
                    'bg-green-100': success,
                    'bg-blue-100': note,
                    'bg-yellow-100': question
                }">

                    <div class="rounded-full p-2 flex items-center justify-center" :class="{
                        'bg-red-500': error,
                        'bg-theme-primary': success,
                        'bg-blue-500': note,
                        'bg-yellow-500': question
                    }">
                        <Icon id="pencil" :source="getSource()" :width="icon_size" />
                    </div>
                </div>

                <div>
                    <span class="font-bold text-2xl lg:text-3xl" :class="{
                        'text-red-500': error,
                        'text-theme-primary': success,
                        'text-blue-500': note,
                        'text-yellow-500': question
                    }">
                        {{ pop_up_text }}
                    </span>
                </div>
            </div>


            <!-- Main Modal Body (Flex-grow content area) -->
            <div class="flex-grow overflow-y-auto px-4 lg:px-6">
                <div
                    class="w-full flex flex-col text-lg lg:text-xl justify-center text-center items-center text-gray-600">
                    <slot></slot>
                </div>

                <!-- Amendment Checkboxes -->
                <div class="flex flex-row space-x-5 lg:space-x-10 p-3" v-if="amendment">
                    <div class="w-full">
                        <Checkbox :checkbox_width="30" id="scheckbox" class="float-right" ref="fcheckbox"
                            @click="checkStatus('fcheckbox')">
                            <span v-text="lang('checkbox.label.yes')"
                                class="text-base uppercase text-theme-primary font-bold ml-2" />
                        </Checkbox>
                    </div>
                    <div class="w-full">
                        <Checkbox :checkbox_width="30" id="scheckbox" class="float-left" ref="scheckbox"
                            checked_image="images/papsi_ck_on_icon_neg.svg" @click="checkStatus('scheckbox')">
                            <span v-text="lang('checkbox.label.no')"
                                class="text-base uppercase text-red-600 font-bold ml-2" />
                        </Checkbox>
                    </div>
                </div>
            </div>


            <!-- Footer Buttons -->
            <div class="flex flex-col lg:flex-row justify-end p-4 gap-4">

                <Button v-if="negativeButton" class="order-2 lg:order-1" :theme="[
                    error
                        ? 'bg-red-500 text-white'
                        : success
                            ? 'bg-white border-2 border-theme-primary text-theme-primary'
                            : note
                                ? 'bg-white border-2 border-blue-500 text-blue-500'
                                : question
                                    ? 'bg-white border-2 border-yellow-500 text-yellow-500'
                                    : 'bg-white border-2 border-theme-primary text-theme-primary'
                ].join(' ')" @clicked="negativeClick" :button_text="negativeButton" :text_size="text_size">
                    {{ negativeButton }}
                </Button>

                <Button v-if="positiveButton" class="order-1 lg:order-2" :theme="[
                    error ? 'bg-red-500' :
                        success ? 'bg-theme-primary' :
                            note ? 'bg-blue-500' :
                                question ? 'bg-yellow-500' :
                                    '',
                    'text-white'
                ].join(' ')" @clicked="positiveClick" :button_text="positiveButton" :disabled="disable_button"
                    :text_size="text_size">
                    {{ positiveButton }}
                </Button>

            </div>


        </div>
    </div>
</template>

<script>
import mixin from '@/mixin';
import { defineComponent } from 'vue'
import Button from "./ButtonControl.vue";
import Checkbox from "./CheckBox.vue";
import Icon from "./Icon.vue"

export default defineComponent({
    props: {
        id: {
            type: String,
            required: true,
        },
        default_width: {
            type: String,
            default: 'max-w-md',
            required: false,
        },
        default_height: {
            type: String,
            default: 'max-h-[85%]',
            required: false,
        },
        type: {
            type: String,
            required: false,
        },
        closable: {
            type: Boolean,
            required: false,
        },
        title: {
            type: String,
            required: false,
            default: null,
        },
        display: {
            type: Boolean,
            required: false,
            default: false,
        },
        positiveButton: {
            type: String,
            required: false,
            default: null,
        },
        amendment: {
            type: String,
            required: false,
            default: null,
        },
        z_value: {
            type: String,
            required: false,
        },
        negativeButton: {
            type: String,
            required: false,
            default: null,
        },
        otpType: {
            type: String,
            required: false,
        },
        withcomponent: {
            type: Boolean,
            default: false,
            required: false,
        },
        question: {
            type: Boolean,
            default: false,
        },
        success: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Boolean,
            default: false,
        },
        note: {
            type: Boolean,
            default: false,
        },
        modal_message: {
            type: String,
            default: null
        },
        text_size: {
            type: String,
            required: false,
        },
        disable_button: {
            type: Boolean,
            default: false,
        }
    },
    components: {
        Button,
        Checkbox,
        Icon,
    },
    data() {
        return {
            show: true,
            popUp: false,
            re_reques_disabled: false,
            request_timer: 120,
            pop_up_text: null,
            icon_size: 0,
            approved_checkbox: false,
            disapproved_checkbox: false,
            Loading: false,
            button_theme_negative: '',
            button_theme_positive: ''
        }
    },
    mixins: [
        mixin,
    ],
    methods: {
        close() {
            this.show = false;
            this.$emit('closed');
        },
        negativeClick() {
            this.$emit('negativeClick');
        },
        positiveClick() {
            const ref = this.$refs;
            let vote;
            if (this.amendment) {
                if (ref.scheckbox.status) {
                    vote = 0;
                    //  ref.scheckbox.toggle();
                }
                if (ref.fcheckbox.status) {
                    vote = 1;
                    // ref.fcheckbox.toggle();
                }
                this.$emit('positiveClick', vote);
            } else {
                this.$emit('positiveClick');
            }
        },
        readAmendments() {
            this.$emit('readAmendments');
        },
        getSource() {
            if (this.success) {
                this.icon_size = 130;
                return 'IconsNew/papsi_check.svg';
            }
            if (this.error) {
                this.icon_size = 180;
                return 'IconsNew/papsi_error.svg';
            }
            if (this.note) {
                this.icon_size = 120;
                return 'IconsNew/papsi_info.svg';
            }
            if (this.question) {
                this.icon_size = 120;
                return 'IconsNew/papsi_info.svg';
            }
        },
        // checkStatus(n) {
        // const ref = this.$refs;

        //    if(n=='fcheckbox'){
        //         this.approved_checkbox =true;
        //         if(ref.scheckbox.status) {
        //             this.disapproved_checkbox = false;
        //             ref.scheckbox.toggle();
        //         }
        //    }
        //    if(n=='scheckbox'){
        //     this.disapproved_checkbox = true;
        //       if(ref.fcheckbox.status)
        //         {
        //         this.approved_checkbox =false;
        //           ref.fcheckbox.toggle();
        //         }
        //    }
        //    if(ref.fcheckbox.status == false && ref.scheckbox.status == false){
        //         this.disable_button = true;
        //    }else{
        //         this.disable_button = false;
        //    }
        // }
    },
    watch: {
        display(n) {
            this.show = n;
            setTimeout(() => {
                if (this.error) {
                    this.pop_up_text = "ERROR";
                }
                if (this.success) {
                    this.pop_up_text = "SUCCESS";
                    this.button_theme_positive = "bg-theme-primary text-white"
                }
                if (this.note) {
                    if (this.modal_message != '' && this.modal_message != null) {
                        this.pop_up_text = "INFORMATION";
                    } else {
                        this.pop_up_text = "INFORMATION";
                    }

                }
                if (this.question) {
                    this.pop_up_text = "CONFIRMATION";
                }
                if (this.question) {
                    this.button_theme_negative = 'bg-white border-2 text-yellow-500 border-yellow-500 text-white';
                    this.button_theme_positive = 'bg-yellow-500 text-white';
                }
                if (this.error) {
                    this.button_theme_negative = 'bg-red-500 text-white';
                }

                if (this.note) {
                    this.button_theme_positive = 'bg-blue-500 text-white';
                }
                this.popUp = n;
            }, 10);
        }
    },
    mounted() {
        this.show = this.display;
        // if(this.amendment){
        //     if(this.approved_checkbox == false && this.disapproved_checkbox == false){
        //         this.disable_button = true;
        //    }
        // }

        if (this.question) {
            this.button_theme_negative = 'bg-white border-2 text-yellow-500 border-yellow-500';
            this.button_theme_positive = 'bg-yellow-500 text-white';
        }
        if (this.error) {
            this.button_theme_negative = 'bg-red-500';
        }

        if (this.note) {
            this.button_theme_positive = 'bg-blue-500 text-white';
        }


    }
})
</script>
<style>
.bg-translucent {
    background-color: rgba(0, 0, 0, .96) !important;
}

.modal-max-h {
    max-height: calc(100vh - 50px);
}

.text-gray-600 {
    color: #4b5563 !important;
}
</style>